/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState, type FormEvent } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import type { ApiResult } from "../api/client";
import {
  addRecipient,
  removeRecipient,
  updateRecipient,
  type DemoCapability,
  type DemoRecipient,
} from "../api/demo";
import { useToast } from "../contexts/ToastContext";
import { Button } from "../components/ui/Button";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { displayPhone } from "../lib/onboarding";
import { ValidationError } from "../lib/validation";

type Mode =
  | { kind: "list" }
  | { kind: "add" }
  | { kind: "edit"; phone: string }
  | { kind: "remove"; phone: string };

const LIST: Mode = { kind: "list" };

function sameNumber(typed: string, phone: string): boolean {
  const lastTen = (value: string) => value.replace(/\D/g, "").slice(-10);
  return lastTen(typed) === lastTen(phone);
}

/** Who can get texts from demo runs. Open on the add form when the list is empty. */
export function DemoPhones({
  isOpen,
  onClose,
  recipients,
  suggestion,
  onChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  recipients: DemoRecipient[];
  suggestion: DemoRecipient | null;
  onChange: (next: DemoCapability) => void;
}) {
  const { showToast } = useToast();
  const [mode, setMode] = useState<Mode>(LIST);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function close() {
    setMode(LIST);
    setRemoveError(null);
    onClose();
  }

  function saved(next: DemoCapability, message: string) {
    onChange(next);
    setMode(LIST);
    setRemoveError(null);
    showToast("success", message);
  }

  async function remove(person: DemoRecipient) {
    setBusy(true);
    setRemoveError(null);
    const result = await removeRecipient(person.phone);
    setBusy(false);
    if (result.ok) {
      saved(result.value, `${person.label} removed`);
    } else {
      setMode(LIST);
      setRemoveError(result.error.message);
    }
  }

  const empty = recipients.length === 0;
  const toRemove =
    mode.kind === "remove" ? recipients.find((person) => person.phone === mode.phone) : undefined;

  return (
    <>
    <Modal
      isOpen={isOpen && toRemove === undefined}
      onClose={close}
      onBeforeClose={() => !busy}
      title="Demo phones"
      subtitle="These phones can get texts from demo runs."
      size="lg"
    >
      <div className="space-y-4 px-6 py-4">
        <ErrorMessage message={removeError} className="mb-0" />
        {!empty && (
          <ul aria-label="Demo phones" className="divide-y divide-border">
            {recipients.map((person) => (
              <li key={person.phone} className="py-3 first:pt-0">
                {mode.kind === "edit" && mode.phone === person.phone ? (
                  <PhoneForm
                    initial={{ ...person, phone: displayPhone(person.phone) ?? "" }}
                    submitLabel="Save"
                    currentPhone={person.phone}
                    submit={(label, phone) => updateRecipient(person.phone, label, phone)}
                    onSaved={(next, label) => saved(next, `${label} saved`)}
                    onCancel={() => setMode(LIST)}
                    busy={busy}
                    onBusy={setBusy}
                  />
                ) : (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-medium text-text-primary">{person.label}</span>
                    <span className="text-text-tertiary">{displayPhone(person.phone)}</span>
                    <span className="ml-auto flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        aria-label={`Edit ${person.label}`}
                        onClick={() => {
                          setRemoveError(null);
                          setMode({ kind: "edit", phone: person.phone });
                        }}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        aria-label={`Remove ${person.label}`}
                        onClick={() => {
                          setRemoveError(null);
                          setMode({ kind: "remove", phone: person.phone });
                        }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {empty || mode.kind === "add" ? (
          <PhoneForm
            initial={empty && suggestion ? suggestion : { label: "", phone: "" }}
            submitLabel="Add"
            currentPhone={null}
            submit={addRecipient}
            onSaved={(next, label) => saved(next, `${label} added`)}
            onCancel={empty ? close : () => setMode(LIST)}
            busy={busy}
            onBusy={setBusy}
          />
        ) : (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => setMode({ kind: "add" })}
          >
            <Plus size={14} />
            Add phone
          </Button>
        )}
      </div>
    </Modal>
    {isOpen && toRemove !== undefined && (
      <ConfirmModal
        title={`Remove ${toRemove.label}?`}
        body="This ends their runs and releases their bookings."
        confirm="Remove"
        busy={busy}
        onClose={() => setMode(LIST)}
        onConfirm={() => remove(toRemove)}
      />
    )}
    </>
  );
}

function PhoneForm({
  initial,
  submitLabel,
  currentPhone,
  submit,
  onSaved,
  onCancel,
  busy,
  onBusy,
}: {
  initial: DemoRecipient;
  submitLabel: string;
  currentPhone: string | null;
  submit: (label: string, phone: string) => Promise<ApiResult<DemoCapability>>;
  onSaved: (next: DemoCapability, label: string) => void;
  onCancel: () => void;
  busy: boolean;
  onBusy: (busy: boolean) => void;
}) {
  const [label, setLabel] = useState(initial.label);
  const [phone, setPhone] = useState(initial.phone);
  const [error, setError] = useState<ValidationError | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    onBusy(true);
    setError(null);
    const name = label.trim();
    const result = await submit(name, phone.trim());
    onBusy(false);
    if (result.ok) {
      onSaved(result.value, name);
      return;
    }
    setError(ValidationError.fromApiError(result.error));
  }

  const general =
    error === null
      ? null
      : (error.getFieldError("people") ??
        (error.fieldErrors.length === 0 ? error.errorMessage : null));
  const renumbered = currentPhone !== null && !sameNumber(phone, currentPhone);

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="space-y-3">
      <ErrorMessage message={general} className="mb-0" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Name"
          value={label}
          error={error?.getFieldError("label") ?? ""}
          onChange={(event) => setLabel(event.target.value)}
        />
        <Input
          label="Phone"
          type="tel"
          placeholder="(303) 555-0142"
          value={phone}
          error={error?.getFieldError("phone") ?? ""}
          onChange={(event) => setPhone(event.target.value)}
        />
      </div>
      {renumbered && (
        <p className="text-xs text-text-tertiary">
          A new number ends this phone's runs and releases its bookings.
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={busy || label.trim() === "" || phone.trim() === ""}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
